import { useCallback } from 'react'
import { useApp } from '@/contexts/AppContext'
import { useAuth } from '@/contexts/AuthContext'
import { useSSE } from './useSSE'
import { ReviewFormData, ReviewReport, type SupportingDocInput } from '@/types'
import type { SelectedDriveFile } from '@/components/wizard/GoogleDrivePicker'
import {
  downloadGoogleDriveFile,
  googleDriveDownloadToFile,
} from '@/lib/api/google-drive'

interface ReviewStartCompletion {
  result: ReviewReport
  reviewId?: string
}

/**
 * Hook for managing the contract review workflow
 */
export function useContractReview() {
  const { state, setError, setViewState, setProgress, setContractText, clearChat } = useApp()
  const { getAccessToken } = useAuth()
  const { streamReview } = useSSE()

  const startReview = useCallback(async (
    formData: ReviewFormData,
    file?: File | null,
    driveFile?: SelectedDriveFile | null,
  ): Promise<ReviewStartCompletion | null> => {
    const fileToAnalyze = file || state.selectedFile
    const driveFileToAnalyze = driveFile ?? null

    if (!fileToAnalyze && !driveFileToAnalyze) {
      setError('Please select a file to analyze.')
      return null
    }

    clearChat()

    setProgress({
      message: 'Initializing...',
      progress: 0,
      isConnected: false,
      logs: [],
    })
    setViewState('progress')

    try {
      let requestData: Record<string, unknown>
      let sourceFile: File

      if (driveFileToAnalyze) {
        setProgress({
          message: 'Importing file from Google Drive...',
          progress: 0,
          isConnected: false,
          logs: [],
        })
        const downloaded = await downloadGoogleDriveFile({
          fileId: driveFileToAnalyze.id,
          resourceKey: driveFileToAnalyze.resourceKey,
          accessToken: driveFileToAnalyze.accessToken,
        })
        sourceFile = googleDriveDownloadToFile(downloaded)
      } else if (fileToAnalyze) {
        sourceFile = fileToAnalyze
      } else {
        setError('Please select a file to analyze.')
        return null
      }

      const [base64, contractText, supportingDocuments] = await Promise.all([
        readFileAsBase64(sourceFile),
        readFileAsText(sourceFile),
        readSupportingDocumentsAsBase64(formData.supportingDocs ?? []),
      ])

      setContractText(contractText)

      requestData = {
        fileBase64: base64,
        fileName: sourceFile.name,
        backgroundInfo: formData.backgroundInfo || 'No background information provided.',
        agentInstructions: formData.agentInstructions || '',
        contractType: formData.contractType,
        userRole: formData.userRole,
        roleInstructions: formData.roleInstructions,
        jurisdiction: formData.jurisdiction,
        skipResearch: formData.skipResearch !== false,
        ...(supportingDocuments.length > 0 ? { supportingDocuments } : {}),
      }

      const accessToken = await getAccessToken()
      const result = await streamReview(requestData, accessToken)
      return result
    } catch (err) {
      console.error('Failed to start review:', err)
      const message = err instanceof Error ? err.message : 'Failed to read file.'
      setError(driveFileToAnalyze ? message : message)
      setViewState('empty')
      return null
    }
  }, [state.selectedFile, setError, setProgress, setViewState, streamReview, setContractText, clearChat, getAccessToken])

  return {
    startReview,
    isProcessing: state.viewState === 'progress',
  }
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string)
      } else {
        reject(new Error('Failed to read file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string)
      } else {
        reject(new Error('Failed to read file'))
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

async function readSupportingDocumentsAsBase64(docs: SupportingDocInput[]) {
  if (!docs.length) return [];

  const encoded = await Promise.all(
    docs.map(async (doc) => ({
      fileName: doc.file.name,
      docType: doc.docType,
      fileBase64: await readFileAsBase64(doc.file),
    }))
  );

  return encoded;
}
